import { EdnaWrapper } from '@providers/EdnaProvider';
import { FC, memo } from 'react';
import styled, { ThemeColor, css } from 'styled-components';
import { getColor } from '../../helpers/colors';
import { IconList } from './constants';
import { IconProps } from './types';

const IconComponent: FC<IconProps> = ({ name, ...props }: IconProps) => {
  const IconComp = IconList?.[name] || IconList.PLACEHOLDER;

  return <IconComp {...props} />;
};

const StyledIcon = styled(IconComponent)<{ $color?: ThemeColor | string }>`
  min-width: ${(props) => props.width};
  min-height: ${(props) => props.height};
  margin: ${(props) => props.margin};
  ${({ $color, theme: { colors } }) => {
    if ($color) {
      return css`
        color: ${getColor($color, colors)};
      `;
    }
  }}
`;
/**
 *  Component used to render Icons. The current list is available via the variable `IconListType`, exported by the package.
 *  During the bundling proccess, all SVG Icons are turned into JSX using [SVGR](https://react-svgr.com/docs/rollup/). This component inherits all SVG props like `width`, `height`, `fill`, etc.
 *  If you are searching for the full array of parameters, the react type used is `React.SVGAttributes<SVGSVGElement>`.
 *
 */

const Icon: FC<IconProps> = ({
  name,
  width = '20px',
  height = '20px',
  margin = '0',
  color,
  ...props
}) => {
  // Future: Analyse if this EdnaWrapper should be kept. For instance, it's being kept for consistency.
  // But as Icons can be rendered a lot on the same screen, some further research could be done in the future when
  // its usage is increased. The ideal scenario here would be using `withEdna` function in the future.
  return (
    <EdnaWrapper>
      <StyledIcon
        name={name}
        role="img"
        aria-label={name}
        width={width}
        height={height}
        margin={margin}
        $color={color}
        {...props}
      />
    </EdnaWrapper>
  );
};

Icon.displayName = 'Icon';

// Export for Storybook-Only, not exposed elsewhere. Memoized components can't show descriptions or default values.
export { Icon };

export default memo(Icon);
